import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import BoxedTitle from "@/components/BoxedTitle";
import ContactListItem from "@/pages/admin/ContactListItem";
import ContactDetail from "@/pages/admin/ContactDetail";
import FilterChips from "@/pages/admin/FilterChips";
import useAdminContacts from "@/pages/admin/useAdminContacts";

export default function AdminInbox() {
  const navigate = useNavigate();
  const {
    key,
    contacts,
    status,
    fetchContacts,
    lock,
    markRead,
    deleteContact,
    selectedIds,
    toggleSelect,
    selectAll,
    clearSelection,
    batchMarkRead,
    batchDelete,
  } = useAdminContacts();

  const [selectedId, setSelectedId] = useState(null);
  const [filter, setFilter] = useState("all");

  // Redirect through /login if not authenticated (or hard-rejected).
  useEffect(() => {
    if (!key || status.state === "unauthorized") {
      navigate("/login?next=/admin/inbox", { replace: true });
    }
  }, [key, status.state, navigate]);

  const filtered = useMemo(() => {
    if (filter === "all") return contacts;
    if (filter === "unread") return contacts.filter((c) => !c.is_read);
    return contacts.filter((c) => c.reason === filter);
  }, [contacts, filter]);

  const unreadCount = useMemo(
    () => contacts.filter((c) => !c.is_read).length,
    [contacts]
  );

  const selected = contacts.find((c) => c.id === selectedId);

  const onSelect = (c) => {
    setSelectedId(c.id);
    if (!c.is_read) markRead(c.id);
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this submission permanently?")) return;
    const ok = await deleteContact(id);
    if (!ok) alert("Delete failed.");
    if (selectedId === id) setSelectedId(null);
  };

  const allFilteredSelected =
    filtered.length > 0 && filtered.every((c) => selectedIds.has(c.id));

  const onToggleSelectAll = () => {
    if (allFilteredSelected) {
      clearSelection();
    } else {
      selectAll(filtered.map((c) => c.id));
    }
  };

  const onBatchMarkRead = async () => {
    const { ok, count } = await batchMarkRead();
    if (!ok) alert("Batch mark-read failed.");
    else if (count === 0) {
      // no-op — silent
    }
  };

  const onBatchDelete = async () => {
    const n = selectedIds.size;
    if (n === 0) return;
    if (!window.confirm(`Delete ${n} submission${n === 1 ? "" : "s"} permanently? This cannot be undone.`)) return;
    const { ok } = await batchDelete();
    if (!ok) alert("Batch delete failed.");
    if (selectedId && selectedIds.has(selectedId)) setSelectedId(null);
  };

  if (!key) return null;

  return (
    <main
      className="mx-auto max-w-[1400px] px-6 lg:px-10 pt-20 lg:pt-28 pb-32"
      data-testid="admin-inbox-page"
    >
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="vbb-overline">
            <Link to="/admin" className="vbb-link" data-testid="crumb-admin">
              Admin
            </Link>{" "}
            / Inbox
          </div>
          <h1
            className="font-display uppercase text-5xl sm:text-6xl lg:text-7xl mt-6 tracking-tighter leading-[0.95] pl-[0.35em]"
            data-testid="admin-inbox-title"
          >
            <BoxedTitle size="lg" thick>
              Inbox.
            </BoxedTitle>
          </h1>
          <p className="mt-3 font-mono-tiny text-[#6b6b6b]">
            {status.state === "loading"
              ? "Loading…"
              : `${contacts.length} total · `}
            {status.state !== "loading" && (
              <span
                className={
                  unreadCount > 0 ? "text-[#e34234]" : "text-[#6b6b6b]"
                }
                data-testid="admin-unread-count"
              >
                {unreadCount} unread
              </span>
            )}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Link
            to="/admin"
            className="vbb-btn vbb-btn-ghost"
            data-testid="admin-back-to-home"
          >
            ← Admin
          </Link>
          <Link
            to="/admin/market"
            className="vbb-btn vbb-btn-ghost"
            data-testid="admin-market-link"
          >
            Market
          </Link>
          <button
            onClick={() => fetchContacts(key)}
            className="vbb-btn vbb-btn-ghost"
            data-testid="admin-refresh-btn"
          >
            Refresh
          </button>
          <button
            onClick={lock}
            className="vbb-btn"
            data-testid="admin-lock-btn"
          >
            Lock
          </button>
        </div>
      </div>

      <FilterChips filter={filter} setFilter={setFilter} />

      {/* Batch action bar — only visible when items are selected */}
      {selectedIds.size > 0 && (
        <div
          className="mt-4 flex flex-wrap items-center justify-between gap-3 p-3 bg-[#0a0a0a] text-[#fafafa] border border-[#0a0a0a]"
          data-testid="admin-batch-bar"
        >
          <span
            className="font-mono-tiny"
            data-testid="admin-batch-count"
          >
            {selectedIds.size} selected
          </span>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={onBatchMarkRead}
              className="px-3 py-1 font-mono-tiny uppercase tracking-wider border border-[#fafafa] hover:bg-[#fafafa] hover:text-[#0a0a0a] transition-colors"
              data-testid="admin-batch-mark-read-btn"
            >
              Mark read
            </button>
            <button
              onClick={onBatchDelete}
              className="px-3 py-1 font-mono-tiny uppercase tracking-wider bg-[#e34234] border border-[#e34234] hover:bg-[#c93529] transition-colors"
              data-testid="admin-batch-delete-btn"
            >
              Delete
            </button>
            <button
              onClick={clearSelection}
              className="px-3 py-1 font-mono-tiny uppercase tracking-wider border border-[#fafafa] opacity-70 hover:opacity-100 transition-opacity"
              data-testid="admin-batch-clear-btn"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {status.state === "error" && (
        <div
          className="mt-8 p-4 border border-[#e34234] text-[#e34234] font-mono-tiny"
          data-testid="admin-error-banner"
        >
          {status.error}
        </div>
      )}

      <div
        className="mt-6 grid grid-cols-1 lg:grid-cols-12 border border-[#0a0a0a]"
        data-testid="admin-inbox-grid"
      >
        <div className="lg:col-span-5 lg:vbb-border-r max-h-[640px] overflow-y-auto">
          {filtered.length > 0 && (
            <label
              className="flex items-center gap-3 px-5 py-3 vbb-border-b bg-[#fafafa] cursor-pointer"
              data-testid="admin-select-all-row"
            >
              <input
                type="checkbox"
                checked={allFilteredSelected}
                onChange={onToggleSelectAll}
                className="w-4 h-4 accent-[#e34234] cursor-pointer"
                data-testid="admin-select-all-checkbox"
              />
              <span className="font-mono-tiny text-[#6b6b6b]">
                {allFilteredSelected
                  ? `Deselect all ${filtered.length}`
                  : `Select all ${filtered.length} visible`}
              </span>
            </label>
          )}
          {filtered.length === 0 && status.state === "ready" && (
            <div
              className="p-10 text-center font-mono-tiny text-[#6b6b6b]"
              data-testid="admin-empty-state"
            >
              No submissions match this filter.
            </div>
          )}
          {filtered.map((c) => (
            <ContactListItem
              key={c.id}
              contact={c}
              active={selectedId === c.id}
              selected={selectedIds.has(c.id)}
              onSelect={onSelect}
              onToggleSelect={toggleSelect}
            />
          ))}
        </div>
        <ContactDetail contact={selected} onDelete={onDelete} />
      </div>
    </main>
  );
}
